import { useMemo, useState, useEffect, useCallback, useContext } from "react";
import { H1, colorsLight, useParsedQueryString, Caption } from "@coherehealth/common";
import { generatePath, useLocation } from "react-router-dom";
import routes from "routes";
import QmBreadcrumbs from "util/QmBreadcrumbs";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import ProductivityFilters from "./ProductivityFilters";
import { DateFilterOptions } from "../util/ChartUtil";
import { useNavigate } from "react-router-dom";
import { User } from "@coherehealth/qm-api";
import { UserContext } from "../../../UserContext";
import ProductivityContent from "./ProductivityContent";
import { useAuthorized } from "Authorization";
import { format } from "date-fns-tz";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    pageContent: {
      padding: "24px 32px 0px",
    },
  })
);

const ProductivityDashboard = () => {
  const location = useLocation();
  const classes = useStyles();
  const navigate = useNavigate();
  const canViewPage = useAuthorized("PRODUCTIVITY_REPORTS");
  const qs = useParsedQueryString();
  const initializedDate = useMemo(() => (qs.days ? (qs.days as DateFilterOptions) : "1"), [qs]);

  const [dateValue, setDateValue] = useState<DateFilterOptions>(initializedDate);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [selectedAgents, setSelectedAgents] = useState<User[]>([]);
  const { queues, activeQueue } = useContext(UserContext);
  const initializedQueue = useMemo(
    () => (qs.queue ? (qs.queue as string) : activeQueue?.id),
    [activeQueue?.id, qs.queue]
  );
  const [queue, setQueue] = useState<string | undefined>(initializedQueue);

  useEffect(() => {
    setDateValue(initializedDate);
    setQueue(initializedQueue);
  }, [initializedDate, initializedQueue]);

  const changeQueryStringValue = useCallback(
    (newDateValue: DateFilterOptions, newQueueValue: string | undefined) => {
      navigate({
        pathname: generatePath(routes.REPORTS.PRODUCTIVITY),
        search: `days=${newDateValue}&queue=${newQueueValue}`,
      });
    },
    [navigate]
  );

  useEffect(() => {
    //sets query string as 1 days as default on initial page load, and queue as active queue
    const shouldSetDays = !qs.days || (qs.days !== "1" && qs.days !== "7" && qs.days !== "14" && qs.days !== "28");
    const shouldSetQueue = !qs.queue || !queues.find((queue) => queue.id === qs.queue);
    if (shouldSetDays || shouldSetQueue) {
      const days = shouldSetDays ? "1" : dateValue;
      const q = shouldSetQueue ? activeQueue?.id : queue;
      changeQueryStringValue(days, q);
    }
  }, [activeQueue?.id, changeQueryStringValue, dateValue, qs.days, qs.queue, queue, queues]);

  const breadCrumpRoutes = useMemo(() => {
    return [
      { to: generatePath(routes.REPORTS.DASHBOARD.concat(location.search)), label: "Reports" },
      { to: generatePath(location.pathname.concat(location.search)), label: "Productivity", disabled: true },
    ];
  }, [location.pathname, location.search]);

  const clear = () => {
    setSelectedAgents([]);
    setSelectedSkills([]);
    changeQueryStringValue("1", activeQueue?.id);
  };
  if (!canViewPage) {
    return null;
  }
  const selectedQueue = queues.find((q) => q.id === queue);
  const updatedDate = new Date();
  updatedDate.setMinutes(updatedDate.getMinutes() - 5);
  updatedDate.setMinutes(0);
  return (
    <div
      style={{ backgroundColor: colorsLight.background.mid, height: "100%", display: "flex", flexDirection: "column" }}
    >
      <div className={classes.pageContent}>
        <QmBreadcrumbs routes={breadCrumpRoutes} />
        <div style={{ display: "inline-flex", gap: "25px" }}>
          <H1 style={{ marginBottom: 24 }}>Productivity</H1>
          <Caption style={{ marginTop: "15px", color: colorsLight.font.secondary }}>
            Last updated on {format(updatedDate, "LL/dd/yyyy")} at {format(updatedDate, "hh:mm z")}
          </Caption>
        </div>
      </div>
      <div style={{ display: "flex", height: "100%" }}>
        <ProductivityFilters
          dateValue={dateValue}
          skills={selectedSkills}
          setSkills={setSelectedSkills}
          changeQueryStringValue={changeQueryStringValue}
          agents={selectedAgents}
          setAgents={setSelectedAgents}
          queue={queue}
          queues={queues}
          clear={clear}
        />
        {selectedQueue && (
          <ProductivityContent
            dateValue={dateValue}
            skills={selectedSkills}
            agents={selectedAgents}
            queue={selectedQueue}
          />
        )}
      </div>
    </div>
  );
};

export default ProductivityDashboard;
